import cn from 'classnames';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { Service } from "@/redux/types.ts";
import styles from './searchBar.module.scss';
import { useShadowRoot } from '../../ShadowComponent.tsx';
import SearchInput from './SearchInput.tsx';
import getDisplayedServices from './getDisplayedServices.ts';
import type { Shortcut } from '../shortcuts/type.ts';
import Popper from '@mui/material/Popper';
import SearchResults from './SearchResults.tsx';
import MobileSearchInput from '../mobileHeader/MobileSearchInput.tsx';
import useScreenSize from '../../../hooks/useScreenSize.ts';
import { MOBILE_BREAKPOINT } from "@/constants.ts";
import { useAskAgentMutation } from "@/redux/api/agentApi.ts";
import { useAgentHistory } from "@/context/agent/useAgentHistory.ts";

const { dropdownContainer, dropdown, dropdownSearchMenu } = styles;
export const SEARCH_BAR_MIN_QUERY_LENGTH = 3;

type SearchBarProps = {
  services: Service[];
  onOpen?: () => void;
  isAuthenticated: boolean;
};

const SearchBar: FC<SearchBarProps> = ({ services, onOpen, isAuthenticated }) => {
  const serviceList = services.filter(s => s.enabled);
  const { appendEntry } = useAgentHistory();
  const [askAgent, { isLoading: isAskAgentPending, error: mutationError }] = useAskAgentMutation();
  const [displayResponse, setDisplayResponse] = useState<string | null>(null);

  const shadowRoot = useShadowRoot();

  const { width, height } = useScreenSize();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clearRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<{ toggle: () => void }>(null);

  const closeBar = () => {
    setDisplayResponse(null);
    setIsOpen(false);
    setQuery('');
  };

  const toggle = (e: Event) => {
    const path = e.composedPath();

    if (searchRef.current && path.includes(searchRef.current)) return;
    if (dropdownRef.current && path.includes(dropdownRef.current)) return;
    if (inputRef.current && path.includes(inputRef.current)) return;
    if (clearRef.current && path.includes(clearRef.current)) return;

    closeBar();
  };

  useEffect(() => {
    document.addEventListener('click', toggle);
    return () => document.removeEventListener('click', toggle);
  }, [shadowRoot]);

  const onSearchClick = () => {
    inputRef.current?.focus();
    setAnchorEl(searchRef?.current);
    setIsOpen(true);
    onOpen?.();
  };

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setQuery('');
    setDisplayResponse(null);
    inputRef.current?.focus();
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    if (displayResponse) {
      setDisplayResponse(null);
    }
  };

  const handleAskAgent = () => {
    if (!isAuthenticated || isAskAgentPending || query.length < SEARCH_BAR_MIN_QUERY_LENGTH) return;

    const trimmedQuery = query.trim();
    askAgent({ input: trimmedQuery })
      .unwrap()
      .then(response => {
        appendEntry(trimmedQuery, response);
        setDisplayResponse(response);
      })
      .catch(() => {
        console.error('Failed to ask agent');
        setDisplayResponse(null);
      });
  };

  const onServiceClick = (option: Service) => {
    if (!option.enabled) return;

    mobileToggleRef.current?.toggle?.();

    window.dispatchEvent(new CustomEvent('framework-select-SearchOption', { detail: option }));
    closeBar();
  };

  const onShortcutClick = (option: Shortcut) => {
    window.location.replace(`/${option.module}${option.path}`);
    closeBar();
  };

  const content = getDisplayedServices(serviceList, query);

  const dropdownStyle =
    width < MOBILE_BREAKPOINT
      ? {
          width: width,
          height: height - 53,
          maxHeight: height - 53,
        }
      : undefined;

  const canAskAgent = isAuthenticated && query.trim().length >= SEARCH_BAR_MIN_QUERY_LENGTH;

  return (
    <div tabIndex={-1} className={cn(dropdownContainer, dropdown)}>
      {width <= MOBILE_BREAKPOINT ? (
        <div className={styles.mobileInput}>
          <MobileSearchInput
            {...{
              query,
              onSearch,
              onClear,
              onSearchClick,
              onAskAgent: handleAskAgent,
              showAskAgentAction: canAskAgent,
              isAskAgentLoading: isAskAgentPending,
              inputRef,
              searchRef,
              clearRef,
              mobileToggleRef,
            }}
          />
        </div>
      ) : (
        <div className={styles.desktopInput}>
          <SearchInput
            {...{
              query,
              onSearch,
              onClear,
              onSearchClick,
              onAskAgent: handleAskAgent,
              showAskAgentAction: canAskAgent,
              isAskAgentLoading: isAskAgentPending,
              inputRef,
              searchRef,
              clearRef,
            }}
          />
        </div>
      )}
      <Popper placement="bottom-start" open={isOpen} anchorEl={anchorEl} disablePortal={true}>
        <div ref={dropdownRef} className={dropdownSearchMenu} style={dropdownStyle}>
          <SearchResults
            content={content}
            onShortcutClick={onShortcutClick}
            onServiceClick={onServiceClick}
            query={query}
            isAgentLoading={isAskAgentPending}
            agentResponse={displayResponse}
            agentError={!!mutationError}
            closeBar={closeBar}
          />
        </div>
      </Popper>
    </div>
  );
};

export default SearchBar;
